"use client";

import { UpsellProductData } from "@/utils/upsellTracking";

export const UPSELL_UNDO_KEY = "cart_upsellUndoData" as const;

export interface UpsellUndoData {
  upsellId: string;
  originalSku: string;
  originalDpaPath: string;
  newSku?: string;
  qty: number;
  purchaseSource: string;
  code?: string;
  timestamp: number;
  trackingData?: UpsellProductData;
  level?: "item" | "cart" | "undefined";
}

export type UpsellUndoStorage = Record<string, UpsellUndoData>;

/**
 * Get all stored undo data from sessionStorage
 */
export const getAllUndoData = (): UpsellUndoStorage => {
  if (typeof window === "undefined") return {};
  try {
    const stored = sessionStorage.getItem(UPSELL_UNDO_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Find undo data matching a product's SKU and level (checks if newSku matches product's current SKU)
 */
export const findUndoDataForProduct = (
  productSku: string,
  level?: "item" | "cart"
): UpsellUndoData | null => {
  const allUndoData = getAllUndoData();
  /*
   * console.log("productSku", productSku);
   * console.log("allUndoData", allUndoData);
   * Find undo data where the newSku matches the product's current SKU and level matches
   */
  for (const data of Object.values(allUndoData)) {
    if (
      data.newSku &&
      data.newSku.replace("-SP", "-S")?.toUpperCase() ===
        productSku.replace("-SP", "-S").toUpperCase()
    ) {
      // If level is specified, only return data that matches the level
      if (level && data.level && data.level !== level) {
        continue;
      }
      return data;
    }
  }
  return null;
};

/**
 * Clear undo data by upsellId
 */
export const clearUndoDataById = (upsellId: string): void => {
  if (typeof window === "undefined") return;
  try {
    const existing = sessionStorage.getItem(UPSELL_UNDO_KEY);
    if (existing) {
      const storage: UpsellUndoStorage = JSON.parse(existing);
      delete storage[upsellId];
      sessionStorage.setItem(UPSELL_UNDO_KEY, JSON.stringify(storage));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Failed to clear upsell undo data:", error);
  }
};

/**
 * Finds and clears a single upsell entry matching a specific product SKU
 */
export const clearUndoDataBySku = (productSku: string): void => {
  if (typeof window === "undefined") return;
  const matchingUndo = findUndoDataForProduct(productSku);
  if (matchingUndo?.upsellId) {
    clearUndoDataById(matchingUndo.upsellId);
  }
};
